<script setup>
import {
  CsIcon,
  CsPopover,
  CsPopoverContent,
  CsPopoverTrigger,
  CsSpinner,
} from 'dashboard/components/ui/commslayer';
import { NodeViewWrapper } from '@tiptap/vue-2';
import { tv } from 'tailwind-variants';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { extenstionKey } from './ImageResize';

const props = defineProps({
  // eslint-disable-next-line vue/require-default-prop
  editor: {
    type: Object,
  },
  node: {
    type: Object,
    required: true,
  },
  // eslint-disable-next-line vue/require-default-prop
  extensions: {
    type: Object,
    required: false,
  },
  updateAttributes: {
    type: Function,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  isUploading: {
    type: Boolean,
    default: false,
  },
  uploadError: {
    type: Boolean,
    default: false,
  },
});

const isPopoverOpen = ref(false);
const imageRef = ref(null);
const containerRef = ref(null);
const isResizing = ref(false);
const resizeStartData = ref(null);
const isUploadingImage = ref(props.isUploading);
const hasUploadError = ref(props.uploadError);

// Manual popover control - only show when user clicks image
const isToolbarVisible = ref(false);

function handleImageClick() {
  // Toggle toolbar visibility when image is clicked
  if (props.selected && !isToolbarVisible.value) {
    isToolbarVisible.value = true;
  }
}

const imageResizeRecipe = tv({
  slots: {
    container: 'relative inline-block',
    image:
      'cursor-pointer rounded-[4px] max-w-full h-auto transition-none !animation-none hover:opacity-90',
    handle:
      'absolute bg-brand-blue-11 border border-white rounded-sm opacity-90 z-10 hidden group-data-[selected=true]:block hover:opacity-100 hover:bg-blue-600',
    handleTopLeft: 'top-[-4px] left-[-4px] w-2 h-2 cursor-nwse-resize',
    handleTopRight: 'top-[-4px] right-[-4px] w-2 h-2 cursor-nesw-resize',
    handleBottomLeft: 'bottom-[-4px] left-[-4px] w-2 h-2 cursor-nesw-resize',
    handleBottomRight: 'bottom-[-4px] right-[-4px] w-2 h-2 cursor-nwse-resize',
    uploadOverlay:
      'absolute inset-0 bg-brand-gray-16/50 backdrop-blur-sm flex items-center justify-center text-white z-20',
  },
  variants: {
    selected: {
      true: {
        container: '',
        image: 'outline-brand-blue-11',
      },
      false: {
        container: '',
      },
    },
  },
  defaultVariants: {
    selected: false,
  },
});

const sizePresets = [
  { label: 'Small', value: 'small', height: 128 },
  { label: 'Medium', value: 'medium', height: 256 },
  { label: 'Large', value: 'large', height: 360 },
  { label: 'Original', value: 'original', height: null },
];

const currentSize = computed(() => {
  const height = props.node.attrs.height;
  if (!height) return 'original';

  const preset = sizePresets.find(size => {
    if (!size.height) return false;
    return Math.abs(height - size.height) < 50; // 50px tolerance
  });

  return preset ? preset.value : 'custom';
});

function handleSizeChange(preset) {
  if (preset.value === 'original') {
    props.updateAttributes({
      width: null,
      height: null,
    });
  } else if (preset.value === 'custom') {
    // Keep current height if it's custom
    // Don't change anything, just close popover
  } else {
    // Simply set the height, let CSS handle width with auto
    props.updateAttributes({
      width: null,
      height: preset.height,
    });
  }
  isPopoverOpen.value = false;
}

function startResize(event, direction) {
  event.preventDefault();
  event.stopPropagation();

  isResizing.value = true;

  const img = imageRef.value;
  const rect = img.getBoundingClientRect();
  const startX = event.clientX;
  const startWidth = rect.width;

  // Get natural aspect ratio
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const aspectRatio = naturalHeight / naturalWidth;

  resizeStartData.value = {
    startX,
    startWidth,
    aspectRatio,
    direction,
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  // Set appropriate cursor based on direction
  const cursorMap = {
    topLeft: 'nwse-resize',
    topRight: 'nesw-resize',
    bottomLeft: 'nesw-resize',
    bottomRight: 'nwse-resize',
  };

  document.body.style.cursor = cursorMap[direction] || 'default';
  document.body.style.userSelect = 'none';
}

function handleMouseMove(event) {
  if (!isResizing.value || !resizeStartData.value) return;

  const { startX, startWidth, aspectRatio, direction } = resizeStartData.value;

  let newWidth;

  const deltaX = event.clientX - startX;

  switch (direction) {
    case 'bottomRight': // Bottom-right corner
      newWidth = Math.max(50, startWidth + deltaX);
      break;
    case 'bottomLeft': // Bottom-left corner
      newWidth = Math.max(50, startWidth - deltaX);
      break;
    case 'topRight': // Top-right corner
      newWidth = Math.max(50, startWidth + deltaX);
      break;
    case 'topLeft': // Top-left corner
      newWidth = Math.max(50, startWidth - deltaX);
      break;
    default:
      return;
  }

  // Calculate height based on width and aspect ratio
  const newHeight = Math.round(newWidth * aspectRatio);

  // Apply only height for visual feedback, width will auto-adjust
  if (imageRef.value) {
    imageRef.value.style.height = `${newHeight}px`;
    imageRef.value.style.width = 'auto';
    imageRef.value.style.maxWidth = '100%';
  }
}

function handleMouseUp() {
  if (!isResizing.value || !resizeStartData.value) return;

  const finalHeight = Number.parseInt(imageRef.value.style.height, 10);

  // Update the node attributes with the new size (only height for backend compatibility)
  props.updateAttributes({
    width: null,
    height: finalHeight,
  });

  // Clean up
  isResizing.value = false;
  resizeStartData.value = null;

  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}

const imageDynamicStyles = computed(() => {
  const styles = {};

  if (props.node.attrs.height) {
    styles.height = `${props.node.attrs.height}px`;
    styles.width = 'auto'; // Natural width based on height
    styles.maxWidth = '100%'; // Won't exceed container
    styles.objectFit = 'contain'; // Maintain aspect ratio without distortion
  }

  if (props.selected) {
    styles['outline-width'] = '2px';
    styles['outline-style'] = 'solid';
  }

  return styles;
});

function handleClickOutside(event) {
  if (props.selected && !isResizing.value && containerRef.value) {
    const clickedInsideContainer = containerRef.value.contains(event.target);

    // Check if clicked inside popover content (teleported element)
    // Use data-component attribute from CsPopoverContent
    const clickedInsidePopover = event.target.closest(
      '[data-component="CsPopoverContent"]'
    );

    if (!clickedInsideContainer && !clickedInsidePopover) {
      // Close toolbar and deselect image
      isToolbarVisible.value = false;
      props.editor.commands.focus();
      props.editor.commands.setTextSelection(props.editor.state.selection.to);
    }
  }
}

const {
  container,
  image,
  handle,
  handleTopLeft,
  handleTopRight,
  handleBottomLeft,
  handleBottomRight,
  uploadOverlay,
} = imageResizeRecipe();

async function handleUploadImage() {
  const imgSrc = props.node.attrs.src;
  const isLocalImage =
    imgSrc && (imgSrc.startsWith('blob:') || imgSrc.startsWith('data:'));
  const onUploadImageCallback =
    props.editor.storage[extenstionKey].onUploadImage;

  if (isLocalImage && typeof onUploadImageCallback === 'function') {
    isUploadingImage.value = true;
    hasUploadError.value = false;

    try {
      const response = await fetch(imgSrc);
      const blob = await response.blob();
      const fileName = props.node.attrs.alt || 'pasted-image.png';
      const file = new File([blob], fileName, { type: blob.type });

      const newSrc = await onUploadImageCallback(file); // Use the correctly accessed callback

      if (newSrc && typeof newSrc === 'string') {
        props.updateAttributes({
          src: newSrc,
        });
      } else {
        hasUploadError.value = true;
      }
    } catch (error) {
      hasUploadError.value = true;
    } finally {
      isUploadingImage.value = false;
    }
  } else if (isLocalImage && typeof onUploadImageCallback !== 'function') {
    isUploadingImage.value = false; // Ensure local state is correct
    hasUploadError.value = false;
  }
}

onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.removeEventListener('mousedown', handleClickOutside);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
});

// Watch for selection changes - close toolbar when image is deselected
watch(
  () => props.selected,
  isSelected => {
    if (!isSelected) {
      isToolbarVisible.value = false;
    }
  }
);

onMounted(() => {
  handleUploadImage();
  document.addEventListener('mousedown', handleClickOutside);
});
</script>

<template>
  <NodeViewWrapper as="div" class="tiptap-image block my-2">
    <CsPopover
      :is-open="isToolbarVisible && props.selected"
      :is-manual="true"
      :is-teleport="false"
      placement="top"
    >
      <CsPopoverTrigger>
        <div
          ref="containerRef"
          :class="container({ selected: props.selected })"
          class="group"
          :data-selected="props.selected || false"
          @click="handleImageClick"
        >
          <img
            ref="imageRef"
            :src="node.attrs.src"
            :alt="node.attrs.alt || ''"
            :title="node.attrs.title || ''"
            :style="imageDynamicStyles"
            :class="image({ selected: props.selected })"
          />

          <!-- Uploading Overlay -->
          <div v-if="isUploadingImage" :class="uploadOverlay()">
            <div class="flex items-center justify-center gap-2">
              <CsSpinner class="w-5 h-5" />

              <span class="text-sm text-white"> Uploading </span>
            </div>
          </div>

          <div
            v-if="hasUploadError && !isUploadingImage"
            :class="uploadOverlay()"
          >
            <CsIcon name="alert-circle" class="w-4 h-4 mr-0.5" />
            <span class="text-xs">
              Image upload failed.
              <span
                class="cursor-pointer hover:underline z-50"
                @click="handleUploadImage"
              >
                Try again
              </span>
            </span>
          </div>

          <!-- Resize handles - only show when selected and editable -->
          <template v-if="selected && !isUploadingImage && !hasUploadError">
            <!-- Corner handles -->
            <div
              :class="[handle(), handleTopLeft()]"
              @mousedown="startResize($event, 'topLeft')"
            />
            <div
              :class="[handle(), handleTopRight()]"
              @mousedown="startResize($event, 'topRight')"
            />
            <div
              :class="[handle(), handleBottomLeft()]"
              @mousedown="startResize($event, 'bottomLeft')"
            />
            <div
              :class="[handle(), handleBottomRight()]"
              @mousedown="startResize($event, 'bottomRight')"
            />
          </template>
        </div>
      </CsPopoverTrigger>

      <CsPopoverContent>
        <div
          class="w-[250px] bg-brand-gray-15 dark:bg-brand-gray-15 dark:border dark:border-brand-gray-14 rounded-lg p-1.5"
        >
          <div class="flex flex-wrap gap-0.5">
            <div
              v-for="preset in sizePresets"
              :key="preset.value"
              :is-active="currentSize === preset.value"
              :is-show-checkmark="true"
              class="flex px-1.5 rounded-md text-sm text-brand-gray-8 hover:bg-brand-gray-14 dark:bg-brand-gray-14 cursor-pointer"
              :class="{ 'bg-brand-gray-14': currentSize === preset.value }"
              @click="handleSizeChange(preset)"
            >
              <div class="flex items-center justify-between w-full text-white">
                <span>{{ preset.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </CsPopoverContent>
    </CsPopover>
  </NodeViewWrapper>
</template>
